import { IMAGES } from '@/assets/images'
import { TagTopic, UserAvatar } from '@/components'
import AlbumCollage from '@/components/AlbumCollage/AlbumCollage'
import { BentoCard, TopicTag } from '@/components/StyledComponents'
import TopicStatMini from '@/components/TopicStatsMini'
import { useDeleteTopic } from '@/hooks/api/dashboard'
import { useAuth, useBrowserQuery, useLocalStorage } from '@/hooks/common'
import { BackgroundImage, RBox } from '@/pages/__styled'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { Topic } from '@/types'
import { PrivacyType, PrivacyTypes, TopicType, TopicTypes } from '@/utils/constants'
import {
  convertToGMT,
  formattedNumber,
  getTopicNameFromTopic,
  getTopicStates,
  getTopicTypeLabel,
  isBabylonType,
} from '@/utils/helper'
import { FavoriteBorder, RemoveRedEyeOutlined } from '@mui/icons-material'
import { Box, capitalize, Chip, Grid, styled, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import SVG from 'react-inlinesvg'
import ReactTimeago from 'react-timeago'

interface TopicItemProps {
  topic: Topic
  onClick: () => void
  onRemoved?: () => void
}

const CardView = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: '1px solid #f1f5f9',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04)',
    borderColor: '#e2e8f0',
  },
}))

const ImageArea = styled(Box)(({ theme }) => ({
  height: 0,
  paddingBottom: '65%',
  position: 'relative',
  borderRadius: '16px 16px 0 0',
  overflow: 'hidden',
  backgroundColor: '#f8fafc',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}))

const FooterArea = styled(Box)(({ theme }) => ({
  padding: '16px 20px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  backgroundColor: '#FFFFFF',
}))

const TopicGridItem = ({ topic, onClick, onRemoved }: TopicItemProps) => {
  const { removeTopic } = useAppStore()

  const deleteTopic = useDeleteTopic(topic.topicID)
  const { user: currentUser } = useAuth()
  const query = useBrowserQuery()
  const topicID = query.get('topicID') || ''

  const topicTypeInfo = TopicTypes[Number(topic.topicType)]
  const privacyData = PrivacyTypes[Number(topic.visibility)]
  if (!topicTypeInfo || !privacyData) return null

  const isMyTopic = topic.userID == currentUser.userID

  useEffect(() => {
    if (deleteTopic.isSuccess) {
      removeTopic(topic.topicID)
      onRemoved && onRemoved()
    }
  }, [deleteTopic.isSuccess])

  const imageURL = isBabylonType(topic.topicType)
    ? topic.firstNote?.egoList?.length
      ? topic.firstNote.egoList[topic.firstNote.egoList.length - 1].photoAlbumURL ||
        topic.firstNote.egoList[topic.firstNote.egoList.length - 1].photoFullURL
      : IMAGES.topicPreviewPlaceholder
    : (topic.latestNote?.egoList || []).at(-1)?.photoAlbumURL ||
      (topic.latestNote?.egoList || []).at(-1)?.photoFullURL ||
      (topic as any).photoSnippet?.at(0)?.photoAlbumURL ||
      (topic as any).photoSnippet?.at(0)?.photoFullURL ||
      IMAGES.topicPreviewPlaceholder

  const renderTopicPreviewImage = () => {
    const links = (topic.latestNote?.egoList || []).map((e) => e.photoFullURL)

    if (!isBabylonType(topic.topicType)) {
      return (
        <Box sx={{ borderRadius: 2, mb: 1 }}>
          {links?.length > 0 ? (
            <AlbumCollage
              rowHeight={links?.length <= 2 ? 200 : 0.5 * 200}
              links={links}
              applyLimit
              limit={5}
            />
          ) : (
            <BackgroundImage
              sx={{ borderRadius: 2, height: '200px' }}
              url={IMAGES.topicPreviewPlaceholder}
            />
          )}
        </Box>
      )
    } else if (isBabylonType(topic.topicType)) {
      return (
        <Box sx={{ borderRadius: 2, mb: 1 }}>
          <BackgroundImage
            sx={{ borderRadius: 2, height: '200px' }}
            url={
              topic.firstNote?.egoList?.length
                ? topic.firstNote.egoList[0].photoFullURL
                : IMAGES.topicPreviewPlaceholder
            }
          />
        </Box>
      )
    }
    return (
      <Box sx={{ borderRadius: 2, mb: 1, height: '200px' }}>
        <BackgroundImage sx={{ borderRadius: 2 }} url={IMAGES.topicPreviewPlaceholder} />
      </Box>
    )
  }

  return (
    <BentoCard onClick={onClick}>
      <Box
        className='bento-image'
        sx={{
          height: '250px',
          backgroundImage: imageURL
            ? `url(${imageURL})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      <Box sx={{ padding: '14px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 1.5,
            borderTop: '1px solid rgba(226, 232, 240, 0.6)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <UserAvatar
              borderRadius={100}
              name={topic.userName}
              url={topic.profilePicThumbURL}
              size={24}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              {isMyTopic ? 'You' : topic.userName}
            </Typography>
            <TopicTag
              size='small'
              themeColor={TopicTypes[Number(topic.topicType)].color}
              label={getTopicTypeLabel(topic.topicType)}
              sx={{ mx: 1 }}
            />
          </Box>

          <TopicStatMini topic={topic} />
        </Box>
      </Box>
    </BentoCard>
  )
}

export default TopicGridItem
